import { type MutableRefObject, useEffect, useState } from 'react';

import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { getClientSideCookieValue } from '@cookies/GetClientSideCookieValue';
import { getClientUserId } from '@cookies/GetClientUserId';
import { activeLeadFormAtom } from '@core/Atoms/Leads/ActiveLeadForm.atom';
import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { tenantAtom } from '@core/Atoms/Tenant/Tenant.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { currentVehicleAtom } from '@core/Atoms/Vehicle/CurrentVehicle.atom';
import { currentVehicleIdAtom } from '@core/Atoms/Vehicle/CurrentVehicleId.atom';
import { isWindowDefined } from '@core/Constants/Window';
import { useInitialMount } from '@core/Hooks/UseInitialMount';
import { generatePhoneNumber } from '@core/Utils/GetPhoneNumber';
import { isInstaMotion } from '@core/Utils/Vehicle/IsInstaMotion';
import Button from '@gds/Button/Button';
import {
  createEventWithCallNumber,
  generateLeadTrackingData,
} from '@lead-forms/Tracking/LeadForms.tracking';
import { useModal } from '@modal/Hooks/UseModal';
import { StatusDisplay } from '@shared-components/StatusDisplay/StatusDisplay';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';

import { dealerNumberAtom } from '../../Atoms/DealerNumber.atom';
import { dealerNumberErrorAtom } from '../../Atoms/DealerNumberError.atom';
import { leadFormIdAtom } from '../../Atoms/LeadFormId.atom';
import { showDealerNumberPdpAtom } from '../../Atoms/ShowDealerNumberPdp.atom';
import { type CtaPlacement, callDealerCtasConfig } from './CallDealer.config';

import styles from './CallDealerCTA.module.css';

export interface Props {
  strings: Record<string, string>;
  isSticky: boolean;
  isVsp?: boolean;
  isEcom: boolean;
  ctaPlacement?: CtaPlacement;
  disabled?: boolean;
  shouldAutoGenNumber?: boolean;
  hasStartedAutoGenerateNo?: MutableRefObject<boolean>;
}

export const CallDealerCTA = ({
  ctaPlacement = 'pdp_call-dealer',
  strings,
  isSticky,
  isVsp,
  isEcom,
  disabled,
  shouldAutoGenNumber,
  hasStartedAutoGenerateNo,
}: Props) => {
  const isInitialMount = useInitialMount();
  const toggleLeadForm = useSetAtom(activeLeadFormAtom);
  const vehicleId = useAtomValue(currentVehicleIdAtom);
  const vehicle = useAtomValue(currentVehicleAtom)!;
  const userId = useAtomValue(userIdAtom);
  const formState = useAtomValue(formStateAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const [isNumberVisible, setIsNumberVisible] = useAtom(showDealerNumberPdpAtom);
  const [dealerNumber, setDealerNumber] = useAtom(dealerNumberAtom);
  const [dealerNumberError, setDealerNumberError] = useAtom(dealerNumberErrorAtom);
  const setLeadFormId = useSetAtom(leadFormIdAtom);
  const tenant = useAtomValue(tenantAtom);
  const [phoneNumber, setPhoneNumber] = useState({
    isLoading: false,
    number: '',
    isError: false,
  });
  const { openModal } = useModal();
  const buttonColor = tenant === 'rac' ? 'primary' : 'secondary';

  const isInvestorStockLayout = tenant !== 'rac' && vehicle?.spec?.d2c && isEcom;
  const ctaConfig = callDealerCtasConfig[ctaPlacement];
  const ctaLocation = ctaPlacement === 'hov_call-dealer' ? 'hov' : 'pdp';
  const isInstamotionVehicle = isInstaMotion(vehicle?.details?.dataSource);

  //should auto generate number
  useEffect(() => {
    if (hasStartedAutoGenerateNo?.current === false && shouldAutoGenNumber) {
      hasStartedAutoGenerateNo.current = true;
      handleClick();
    }
  }, [hasStartedAutoGenerateNo, shouldAutoGenNumber]);

  useEffect(() => {
    if (!isInitialMount) {
      const { isError, number, isLoading } = phoneNumber;

      if (isError) {
        setDealerNumberError(true);

        if (ctaConfig.shouldShowRequestCallbackModal(window.innerWidth, isSticky)) {
          toggleLeadForm('request-callback');
        }
      } else {
        if (number && !isLoading) {
          const query = window.matchMedia(`(max-width: 767px)`);
          if (query.matches) {
            window.open(`tel:${number}`, '_self');
          }
        }
      }
    }
  }, [phoneNumber]);

  const { callStart, callSubmit } = generateLeadTrackingData({
    searchParams,
    userId,
    formState,
    leadId: '',
    listingId: vehicleId,
    vehicle,
    leadFormId: 'call-dealer',
    ctaLocation,
    leadAction: 'start',
  });
  const facebookPixelValue = getClientSideCookieValue('_fbp');
  const facebookClickIdValue = getClientSideCookieValue('_fbc');

  const handleClick = async () => {
    if (isNumberVisible) {
      const callSubmitEvent = createEventWithCallNumber(
        vehicleId!,
        phoneNumber.number,
        ctaLocation,
      );
      trackCustomEvent({ event: callSubmitEvent!, context: callSubmit.context });
      window.open(`tel:${phoneNumber.number}`, '_self');
      return;
    }

    trackCustomEvent({ event: callStart.event!, context: callStart.context });
    setPhoneNumber({
      ...phoneNumber,
      isLoading: true,
    });
    const payload = {
      metaData: {
        trackingId: getClientUserId(),
        vehicle_uuid: vehicleId,
        leadSource: tenant || 'web',
        gclid: searchParams?.gclid,
        utmSource: searchParams?.utm_source,
        utmMedium: searchParams?.utm_medium,
        utmCampaign: searchParams?.utm_campaign,
        cItemId: searchParams?.cItemId,
        userId: userId,
        msclkId: searchParams?.msclkid,
        fbc: facebookClickIdValue,
        fbp: facebookPixelValue,
      },
    };

    if (isInstamotionVehicle) {
      const displayNumber = '+49 3030809241';
      setPhoneNumber({
        ...phoneNumber,
        number: displayNumber,
        isLoading: false,
      });
      setDealerNumber(displayNumber);
      if (window.innerWidth > 768) {
        setIsNumberVisible(true);
      }
      return;
    }

    try {
      const reCaptchaLoaded = !!window.grecaptcha;

      if (reCaptchaLoaded) {
        try {
          const data = await generatePhoneNumber(payload);

          // Criteo Pixel Synchronization Script
          if (tenant !== 'rac') {
            const trackingId = data.trackingId;
            const img = document.createElement('img');
            img.setAttribute(
              'src',
              `//gum.criteo.com/sync?c=69&a=1&r=1&u=https%3A%2F%2Fanalytics.freespee.com%2Fexternal%2Fc_pixel%3Fcriteoid%3D%40USERID%40%26mp_id%3D${trackingId}`,
            );
            img.setAttribute('style', 'position: absolute; height: 0px; width: 0px;');
            document.head.appendChild(img);
          }

          if (window.innerWidth < 768) {
            const callSubmitEvent = createEventWithCallNumber(
              vehicleId!,
              phoneNumber.number,
              ctaLocation,
            );
            trackCustomEvent({ event: callSubmitEvent!, context: callSubmit.context });
          }
          setPhoneNumber({
            ...phoneNumber,
            number: data?.displayNumber,
            isLoading: false,
          });
          setDealerNumber(data?.displayNumber);
          if (window.innerWidth > 768) {
            setIsNumberVisible(true);
          }
        } catch (_error) {
          setPhoneNumber({
            ...phoneNumber,
            isLoading: false,
            isError: true,
          });
        }
      } else {
        showErrorModal();
      }
    } catch (_error) {
      // Displays error if there is some issue with Captcha script loading

      showErrorModal();
    }
  };

  const showErrorModal = () => {
    openModal(
      <StatusDisplay
        messages={{
          description: strings.leadFormError,
        }}
        type="error"
      />,
      {
        header: strings[ctaConfig.labelId(false)],
        modalClassName: styles.modalContainer,
      },
    );
  };

  const handleCallbackClick = () => {
    setLeadFormId('request-callback');
    const {
      leadStart: { event, context },
    } = generateLeadTrackingData({
      searchParams,
      userId,
      formState,
      leadFormId: 'request-callback',
      leadId: '',
      listingId: vehicleId,
      vehicle,
      ctaLocation: 'pdp',
      leadAction: 'start',
    });
    trackCustomEvent({ event, context });
    toggleLeadForm('request-callback');
  };
  const openingTimes = vehicle?.dealer?.openingTimes || [];
  const shouldShowCta = isInstamotionVehicle || Boolean(ctaConfig.shouldShowCta({ openingTimes }));

  const hasButtonText = ctaConfig.showButtonText({ isSticky, isVsp, isEcom });

  const ctaText = () => {
    if (isWindowDefined && window.innerWidth > 768) {
      if (isNumberVisible && !isInvestorStockLayout) {
        return dealerNumber;
      }
      if (isInvestorStockLayout) {
        return strings.call;
      }
    }

    if (hasButtonText) {
      return isInvestorStockLayout
        ? strings.call
        : strings[ctaConfig.labelId(isInstamotionVehicle)];
    }

    return '';
  };

  if (!shouldShowCta) return null;

  if (ctaConfig.requestCallback(dealerNumberError, openingTimes) && !isInstamotionVehicle) {
    return (
      <>
        {ctaConfig.shouldShowPhoneNumberError(dealerNumberError, isSticky) && (
          <div className={styles.error}>{strings.callDealerError}</div>
        )}
        <Button
          leftIcon={ctaConfig.requestCallbackLeftIcon(isSticky, isEcom, isVsp)}
          variant={'outlined'}
          color={buttonColor}
          onClick={handleCallbackClick}
          loading={false}
          disabled={disabled}
        >
          {ctaConfig.requestCallbackButtonLabel(strings, isSticky, isEcom)}
        </Button>
      </>
    );
  }

  if (!dealerNumberError)
    return (
      <Button
        leftIcon={
          (isSticky && isEcom && !isVsp && !phoneNumber.isLoading) || isNumberVisible
            ? ctaConfig.buttonIcon
            : null
        }
        dataTestId="callDealerButton"
        disabled={disabled}
        variant={ctaConfig.variant()}
        color={buttonColor}
        loading={phoneNumber.isLoading}
        onClick={handleClick}
      >
        {ctaText()}
      </Button>
    );
};
